import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { usePmt3FarmingResultsData } from '@/hooks/usePmt3FarmingResultsData';
import { CheckCircle, AlertCircle, Link as LinkIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';

const campaignNames = [
  'Overall',
  'Broad',
  'CTA',
  'Full Auto Complements',
  'Full Auto Loose-Match',
  'Full Auto Substitutes',
  'Full Auto Close-Match',
];

const columnHeaders = [
  'Campaign',
  'Active Status',
  'Previous State',
  'Spend Status',
  'Previous Status',
  'Spend',
  'New CSTs',
  'Previous New CSTs',
  'Cost Per CST',
  'Previous Cost/CST',
  'Target $ per CST',
  'Previous Target $/CST',
  'Difference $',
  'Final Bid',
];

const FarmingResultsDashboard: React.FC = () => {
  const { selectedProduct } = usePMT3Selection();
  const { data, isLoading, error } = usePmt3FarmingResultsData(
    selectedProduct?.companyName,
    selectedProduct?.id
  );

  const renderDifference = (costPerCst: number | null, targetCostPerCst: number | null) => {
    if (costPerCst === null || targetCostPerCst === null) return '-';
    const diff = costPerCst - targetCostPerCst;
    const formatted = formatCurrency(diff, 2);
    if (diff < 0) {
      return (
        <span className="flex items-center justify-end space-x-1 text-green-700 dark:text-green-500">
          <CheckCircle size={14} className="shrink-0" />
          <span>{formatted}</span>
        </span>
      );
    } else if (diff > 0) {
      return (
        <span className="flex items-center justify-end space-x-1 text-red-600 dark:text-red-500">
          <AlertCircle size={14} className="shrink-0" />
          <span>{formatted}</span>
        </span>
      );
    } else {
      return formatted;
    }
  };

  const highlightIfDifferent = (current: any, previous: any, formatter = (v: any) => v) => {
    if (previous === null || previous === undefined || previous === '') return '-';
    if (current === previous || String(current) === String(previous)) return '-';
    return (
      <span className="bg-orange-100 dark:bg-orange-800 px-1.5 py-0.5 rounded text-orange-800 dark:text-orange-100">
        {formatter(previous)}
      </span>
    );
  };

  const [clientUrl, setClientUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchUrl = async () => {
      if (!selectedProduct?.companyName) {
        setClientUrl(null);
        return;
      }
      try {
        const urlDocRef = doc(db, 'pmt2_clienturldatabase', selectedProduct.companyName, 'items', 'FarmingResultsDashboard');
        const urlSnap = await getDoc(urlDocRef);
        if (urlSnap.exists()) {
          const urlVal = urlSnap.data().value || urlSnap.data().url || null;
          setClientUrl(urlVal);
        } else {
          setClientUrl(null);
        }
      } catch (error) {
        console.error('Error fetching Farming Results Dashboard URL:', error);
        setClientUrl(null);
      }
    };
    fetchUrl();
  }, [selectedProduct?.companyName]);

  return (
    <Card>
      <CardHeader className="p-4">
        <div className="flex items-center gap-2">
          <CardTitle className="text-2xl font-bold">Farming Results</CardTitle>
          {clientUrl && (
            <a
              href={clientUrl}
              target="_blank"
              rel="noopener noreferrer"
              title="Open Farming Results Dashboard"
              className="text-blue-500 hover:text-blue-700 inline-flex items-center justify-center"
            >
              <LinkIcon size={18} />
            </a>
          )}
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto pb-6">
        {error && (
          <div className="text-red-600 dark:text-red-400 mb-4">Error loading data: {error.message}</div>
        )}
        <Table className="min-w-max">
          <TableHeader>
            <TableRow>
              {columnHeaders.map((header) => (
                <TableHead key={header} className="whitespace-nowrap text-xs py-2 px-3">
                  {header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaignNames.map((campaign) => {
              const c = data?.campaigns?.[campaign];
              return (
                <TableRow key={campaign}>
                  <TableCell className="whitespace-nowrap text-xs py-2 px-3 font-semibold">{campaign}</TableCell>

                  {/* Active Status */}
                  <TableCell className="text-xs py-2 px-3">
                    {isLoading ? <Skeleton className="h-4 w-12" /> : c?.activeStatus ?? '-'}
                  </TableCell>

                  {/* Previous Active Status */}
                  <TableCell className="text-xs py-2 px-3">
                    {isLoading ? <Skeleton className="h-4 w-12" /> : highlightIfDifferent(c?.activeStatus, c?.previousActiveStatus)}
                  </TableCell>

                  {/* Spend Status */}
                  <TableCell className="text-xs py-2 px-3">
                    {isLoading ? <Skeleton className="h-4 w-12" /> : c?.spendStatus ?? '-'}
                  </TableCell>

                  {/* Previous Spend Status */}
                  <TableCell className="text-xs py-2 px-3">
                    {isLoading ? <Skeleton className="h-4 w-12" /> : highlightIfDifferent(c?.spendStatus, c?.previousSpendStatus)}
                  </TableCell>

                  {/* Spend */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : formatCurrency(c?.spend, 2)}
                  </TableCell>

                  {/* New CSTs */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : formatNumber(c?.newCsts, 0)}
                  </TableCell>

                  {/* Previous New CSTs */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : highlightIfDifferent(c?.newCsts, c?.previousNewCsts, (v) => formatNumber(v, 0))}
                  </TableCell>

                  {/* Cost Per CST */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : formatCurrency(c?.costPerCst, 2)}
                  </TableCell>

                  {/* Previous Cost Per CST */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : highlightIfDifferent(c?.costPerCst, c?.previousCostPerCst, (v) => formatCurrency(v, 2))}
                  </TableCell>

                  {/* Target $ per CST */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : formatCurrency(c?.targetCostPerCst, 2)}
                  </TableCell>

                  {/* Previous Target $ per CST */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : highlightIfDifferent(c?.targetCostPerCst, c?.previousTargetCostPerCst, (v) => formatCurrency(v, 2))}
                  </TableCell>

                  {/* Difference $ */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : renderDifference(c?.costPerCst ?? null, c?.targetCostPerCst ?? null)}
                  </TableCell>

                  {/* Final Bid */}
                  <TableCell className="text-xs py-2 px-3 text-right">
                    {isLoading ? <Skeleton className="h-4 w-12 ml-auto" /> : formatCurrency(c?.finalBid, 2)}
                  </TableCell>
                </TableRow>
              );
            })}
            {/* Spacer row */}
            <TableRow>
              {columnHeaders.map((_, idx) => (
                <TableCell key={`spacer-${idx}`} className="py-4"></TableCell>
              ))}
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

function formatCurrency(value: any, decimals = 0): string {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;
}

function formatNumber(value: any, decimals = 0): string {
  if (value === null || value === undefined || value === '') return '-';
  const num = Number(value);
  if (isNaN(num)) return '-';
  return num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default FarmingResultsDashboard;